import { useCallback, useEffect, useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ContractTypeSelect } from "@/components/form/ContractTypeSelect";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon";
import { SupportingDocumentsUploader } from "@/components/wizard/SupportingDocumentsUploader";
import { getGoogleDriveStatus } from "@/lib/api/google-drive";
import { getEmailStatus } from "@/lib/api/email";
import { EmailPicker, type EmailImportPayload } from "./EmailPicker";
import { GoogleDrivePicker, type SelectedDriveFile } from "./GoogleDrivePicker";
import type { SupportingDocInput } from "@/types";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function isAllowedContractFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === "application/pdf" ||
    file.type === DOCX_MIME ||
    name.endsWith(".pdf") ||
    name.endsWith(".docx")
  );
}

interface Step1UploadProps {
  selectedFile: File | null;
  onFileSelect: (file: File | null) => void;
  selectedDriveFile: SelectedDriveFile | null;
  onDriveFileSelect: (file: SelectedDriveFile | null) => void;
  contractType: string;
  onContractTypeChange: (value: string) => void;
  backgroundInfo: string;
  onBackgroundInfoChange: (value: string) => void;
  supportingDocs: SupportingDocInput[];
  onSupportingDocsChange: (docs: SupportingDocInput[]) => void;
}

export function Step1Upload({
  selectedFile,
  onFileSelect,
  selectedDriveFile,
  onDriveFileSelect,
  contractType,
  onContractTypeChange,
  backgroundInfo,
  onBackgroundInfoChange,
  supportingDocs,
  onSupportingDocsChange,
}: Step1UploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [driveAvailable, setDriveAvailable] = useState(false);
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadDriveStatus() {
      try {
        const status = await getGoogleDriveStatus();
        if (!cancelled) {
          setDriveAvailable(status.connected && !status.reconnectRequired);
        }
      } catch {
        if (!cancelled) {
          setDriveAvailable(false);
        }
      }
    }

    void loadDriveStatus();
    async function loadEmailStatus() {
      try {
        const status = await getEmailStatus();
        if (!cancelled) setEmailAvailable(status.microsoft.connected || status.imap.connected);
      } catch {
        if (!cancelled) setEmailAvailable(false);
      }
    }
    void loadEmailStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (isAllowedContractFile(file)) {
          onDriveFileSelect(null);
          onFileSelect(file);
        }
      }
    },
    [onDriveFileSelect, onFileSelect]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (isAllowedContractFile(file)) {
          onDriveFileSelect(null);
          onFileSelect(file);
        }
      }
    },
    [onDriveFileSelect, onFileSelect]
  );

  const handleDriveSelect = useCallback(
    (file: SelectedDriveFile) => {
      onFileSelect(null);
      onDriveFileSelect(file);
    },
    [onDriveFileSelect, onFileSelect]
  );

  const handleClearSelection = useCallback(() => {
    onFileSelect(null);
    onDriveFileSelect(null);
  }, [onDriveFileSelect, onFileSelect]);

  const handleEmailImport = useCallback(
    (payload: EmailImportPayload) => {
      const threadFile = new File([payload.threadText], `${payload.conversation.subject || "email-thread"}.txt`, {
        type: "text/plain",
      });
      const importedDocs: SupportingDocInput[] = [
        {
          id: `email-thread-${Date.now()}`,
          file: threadFile,
          docType: "background",
        },
        ...payload.files
          .filter(({ attachment }) => attachment.id !== payload.contractFile?.attachment.id)
          .slice(0, 4)
          .map(({ file }, index) => ({
            id: `email-attachment-${Date.now()}-${index}`,
            file,
            docType: "background" as const,
          })),
      ];
      const availableSlots = Math.max(0, 5 - supportingDocs.length);
      onSupportingDocsChange([...supportingDocs, ...importedDocs.slice(0, availableSlots)]);
      if (payload.contractFile) {
        onDriveFileSelect(null);
        onFileSelect(payload.contractFile.file);
      }
    },
    [onDriveFileSelect, onFileSelect, onSupportingDocsChange, supportingDocs],
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const hasSelection = selectedFile !== null || selectedDriveFile !== null;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-200">
          Contract Document
        </label>

        {!hasSelection ? (
          <div className="space-y-3">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                "relative rounded-xl border-2 border-dashed p-8 transition-all duration-300",
                isDragging
                  ? "border-primary bg-[var(--dropzone-bg)]"
                  : "border-[var(--dropzone-border)] bg-[var(--dropzone-bg)] hover:border-[rgba(147,51,245,0.4)] hover:bg-[rgba(147,51,245,0.06)] dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-slate-600 dark:hover:bg-slate-800/50"
              )}
            >
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileInput}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
              <div className="flex flex-col items-center justify-center text-center">
                <div
                  className={cn(
                    "mb-4 flex h-16 w-16 items-center justify-center rounded-full transition-all duration-300",
                    isDragging
                      ? "bg-primary/20 text-primary"
                      : "bg-[var(--surface-sunken)] text-[#576274] dark:bg-slate-800 dark:text-slate-400"
                  )}
                >
                  <Upload className="h-8 w-8" />
                </div>
                <p className="text-sm font-medium text-slate-200">
                  Drop your PDF or Word document here or click to browse
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Supports PDF and DOCX files up to 50MB
                </p>
              </div>
            </div>

            {driveAvailable ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-800" />
                  <span className="text-xs uppercase tracking-wide text-slate-500">or</span>
                  <div className="h-px flex-1 bg-slate-800" />
                </div>
                <button
                  type="button"
                  onClick={() => setDrivePickerOpen(true)}
                  className={cn(
                    "flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-all duration-300",
                    "border-[var(--dropzone-border)] bg-[var(--dropzone-bg)] hover:border-[rgba(147,51,245,0.4)] hover:bg-[rgba(147,51,245,0.06)] dark:border-slate-700 dark:bg-slate-900/50 dark:hover:border-slate-600 dark:hover:bg-slate-800/50"
                  )}
                >
                  <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-slate-800 text-slate-200">
                    <GoogleDriveIcon className="h-8 w-8" />
                  </div>
                  <p className="text-sm font-medium text-slate-200">Select from Google Drive</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Choose files from your connected Google Drive
                  </p>
                </button>
              </>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-4 rounded-xl border border-slate-700 bg-slate-900/50 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium text-slate-200">
                  {selectedFile?.name ?? selectedDriveFile?.name}
                </p>
                {selectedDriveFile ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-violet-300">
                    <GoogleDriveIcon className="h-3 w-3" />
                    Drive
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-slate-500">
                {selectedFile
                  ? formatFileSize(selectedFile.size)
                  : selectedDriveFile?.size
                    ? formatFileSize(selectedDriveFile.size)
                    : "Google Drive file"}
              </p>
            </div>
            <button
              onClick={handleClearSelection}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <ContractTypeSelect
        value={contractType}
        onChange={onContractTypeChange}
      />

      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-200">
          Background Information
          <span className="ml-1 text-xs font-normal text-slate-500">(Optional)</span>
        </label>
        <textarea
          value={backgroundInfo}
          onChange={(e) => onBackgroundInfoChange(e.target.value)}
          placeholder="Provide any relevant context about this contract (e.g., counterparty details, business relationship, key concerns)..."
          rows={4}
          className="w-full resize-none rounded-lg border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      <SupportingDocumentsUploader
        docs={supportingDocs}
        onChange={onSupportingDocsChange}
        onEmailClick={emailAvailable ? () => setEmailPickerOpen(true) : undefined}
      />

      <GoogleDrivePicker
        open={drivePickerOpen}
        onOpenChange={setDrivePickerOpen}
        onSelect={handleDriveSelect}
      />
      <EmailPicker
        open={emailPickerOpen}
        onOpenChange={setEmailPickerOpen}
        destination="background"
        onImport={handleEmailImport}
      />
    </div>
  );
}
